import {
  Hash,
  Type as TypeIcon,
  ListOrdered,
  Sigma,
  Cpu,
  Layers,
  Activity,
  Box,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ResourceType } from '@/api/types';
import { cn } from '@/lib/utils';

interface TypeDef {
  key: ResourceType;
  label: string;
  description: string;
  icon: LucideIcon;
}

// Order chosen to put the most-used types first; matches the order in
// pkg/storage/interface.go but with LicenseCounter promoted.
export const RESOURCE_TYPES: TypeDef[] = [
  {
    key: 'LicenseCounter',
    label: 'License Counter',
    description: 'Integer counter — a countable license or seat budget.',
    icon: Hash,
  },
  {
    key: 'String',
    label: 'String',
    description: 'A static value returned to every consumer (config, URL).',
    icon: TypeIcon,
  },
  {
    key: 'StringList',
    label: 'String List',
    description: 'Pool of named items — e.g. IP addresses or GPU names.',
    icon: ListOrdered,
  },
  {
    key: 'Double',
    label: 'Double',
    description: '64-bit floating-point counter for fractional budgets.',
    icon: Sigma,
  },
  {
    key: 'Memory',
    label: 'Memory',
    description: 'Byte quantity with human units (1GB, 512MiB, 2048).',
    icon: Cpu,
  },
  {
    key: 'Collection',
    label: 'Collection',
    description: 'Ordered list of structured items in YAML or JSON.',
    icon: Layers,
  },
  {
    key: 'Measurement',
    label: 'Measurement',
    description: 'Script run on a schedule whose result is the value.',
    icon: Activity,
  },
];

// Maps a resource type to its icon. Shared so the Type picker, the Quotas
// restriction-rule column, and anywhere else that shows a resource speak one
// icon vocabulary. Falls back to a neutral box for unknown / forward-compat
// types (the ResourceType union has a string fall-through).
export function iconForResourceType(
  type: ResourceType | string | null | undefined,
): LucideIcon {
  return RESOURCE_TYPES.find((t) => t.key === type)?.icon ?? Box;
}

interface TypePickerProps {
  selected: ResourceType | null;
  onSelect: (type: ResourceType) => void;
  disabled?: boolean;
}

export default function TypePicker({
  selected,
  onSelect,
  disabled,
}: TypePickerProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Resource type"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3"
    >
      {RESOURCE_TYPES.map((t) => {
        const Icon = t.icon;
        const isSelected = selected === t.key;
        return (
          <button
            key={t.key}
            type="button"
            role="radio"
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onSelect(t.key)}
            className={cn(
              'group text-left rounded-lg border-2 bg-card p-4 transition-all',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              isSelected
                ? 'border-primary shadow-sm'
                : 'border-border hover:border-primary/40 hover:bg-muted/30',
              disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  'shrink-0 rounded-md p-2 transition-colors',
                  isSelected
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground group-hover:text-foreground',
                )}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-sm leading-tight">
                  {t.label}
                </div>
                <div className="mt-1 text-xs text-muted-foreground leading-snug">
                  {t.description}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
